import { routeConstants } from "src/utils/constants";
import { hasBillingActiveSubscription } from "src/utils/billingData";
import getStore from "src/utils/redux/store";
import {
  hasActiveMedProgram,
  hasActiveWeightPremiumProgram,
} from "src/utils/userSegment/features";
import goto from ".";
import { noomMedOfferLayer, productSwitchLayer } from "./constants";
import { getSessionState, updateSessionState } from "../session";
import { getRouteId } from "src/utils/meristemContext";
import { getURLParams, updateForwardedParams } from "src/utils/urlParams";
import { disableGlobalExit } from "src/utils/globalExit";

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    freemiumOnboardingInitialRoute?: string;
    freemiumOnboardingSourceId?: string;
  }
}

function prepareFreemiumExit() {
  const { freemiumOnboardingInitialRoute } = getSessionState("browser");
  updateForwardedParams({ route: freemiumOnboardingInitialRoute });
  disableGlobalExit();
  return freemiumOnboardingInitialRoute || "app-free-onboarding";
}

function freemiumUpsell(route: routeConstants) {
  const currentRouteId = getRouteId();
  const urlParams = getURLParams();
  const currentRoute = urlParams.route || currentRouteId;

  updateSessionState("browser", {
    freemiumOnboardingInitialRoute: currentRoute,
    freemiumOnboardingSourceId: "app-free-onboarding",
  });

  goto.layer("landing", "app-free-onboarding", false, route);
}

export default {
  freemiumUpsellBranched() {
    freemiumUpsell(routeConstants.freemiumBranched);
  },

  freemiumUpsellWeight(utmSource?: string) {
    if (utmSource) {
      updateForwardedParams({ utm_source: utmSource });
    }
    freemiumUpsell(routeConstants.freemiumWeight);
  },

  freemiumUpsellClinical() {
    freemiumUpsell(routeConstants.freemiumClinical);
  },

  /**
   * Redirects Weight / Premium / Med subscribers out of the freemium clinical
   * or health upsell flow. Gated by
   * `hasActiveWeightPremiumProgram` / `hasActiveMedProgram` (plus freemium route
   * segments) on flow entry — see Clinical.Landing.json.
   *
   * Eligibility and destination use Product Catalog `productName`:
   * - Med (`telehealth-medtier-*`) → product switch
   * - Weight / Premium (contains `healthy-weight`) → Noom Med offer
   * - Anything else → skip so the freemium upsell can continue
   *
   * Implemented as a goto (not an action) so PageSet navigation stops here and
   * does not continue into later pages while a layer redirect is still pending.
   */
  redirectToNoomMedOffer() {
    const state = getStore().getState();
    const { billingData } = state;
    const subscription = billingData.activeSubscription?.data;

    if (
      !subscription ||
      !hasBillingActiveSubscription(billingData) ||
      !(hasActiveWeightPremiumProgram(state) || hasActiveMedProgram(state))
    ) {
      // Goto pages do not auto-advance; skip past this entry so the flow continues.
      goto.entryPoint("landing", "skipRedirectToNoomMedOffer");
      return;
    }

    updateForwardedParams({
      route: null,
      subscription_id: subscription.id,
    });

    goto.layer(
      hasActiveMedProgram(state) ? productSwitchLayer : noomMedOfferLayer,
      null,
      true
    );
  },

  exitToFreemiumOnboarding(entryPoint = "freemiumOnboardingHealthGoals") {
    const route = prepareFreemiumExit();
    goto.mainSurvey(false, route, entryPoint);
  },

  exitToFreeGlp1CompanionOnboarding(entryPoint: string) {
    updateForwardedParams({ utm_source: null });
    const route = prepareFreemiumExit();
    goto.layer(
      "app-free-onboarding-glp1-companion",
      null,
      false,
      route,
      entryPoint
    );
  },

  exitFreemiumUpsell() {
    // Clear the global exit flag from sessionStorage
    disableGlobalExit();
    goto.exitBuyflow("USERPORTAL");
  },
};
